import styled from "styled-components";
import { RecommendedActionCard } from "../types/ask";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import ArrowRightIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ArrowRightIconPath";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useMemo, useState } from "react";
import { useMediasByStoreId } from "../hooks/useMediasByStoreId";
import { LoadMore } from "@coworker/apprestructured/src/shared/components/LoadMore/LoadMore";

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const PAGE_SIZE = 10;

const CardWrapper = styled.div`
  margin-bottom: 16px;
`;

const InlineMessageWrapper = styled.div`
  margin-bottom: 12px;
`;

const Description = styled.p`
  font-size: 14px;
  color: var(--grey900);
  line-height: 1.5;
  margin: 0 0 12px;
`;

const MediaLinkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--grey100);
  border-radius: 4px;
  padding: 12px 16px;
  cursor: pointer;
`;

const MediaLinkText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MediaLinkName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--grey900);
`;

const MediaLinkSub = styled.div`
  font-size: 13px;
  color: var(--grey600);
`;

// ---------------------------------------------------------------------------
// Single recommended action card
// ---------------------------------------------------------------------------

function RecommendedActionCardItem({
  card,
  mediaId,
}: {
  card: RecommendedActionCard;
  mediaId: string | null;
}) {
  const navigate = useFixaNavigate();

  const handleMediaClick = () => {
    if (mediaId) {
      navigate(`/mediamaintenance/tabs/${mediaId}`);
    }
  };

  return (
    <CardWrapper>
      <InlineMessageWrapper>
        <FixaInlineMessage
          variant="cautionary"
          title={card.title}
          body={card.subtitle}
        />
      </InlineMessageWrapper>

      {card.description && <Description>{card.description}</Description>}

      <MediaLinkRow onClick={handleMediaClick}>
        <MediaLinkText>
          <MediaLinkName>{card.media_name}</MediaLinkName>
          <MediaLinkSub>
            Room setting · {card.item_count}{" "}
            {card.item_count === 1 ? "item" : "items"}
          </MediaLinkSub>
        </MediaLinkText>
        <FixaSSRIcon paths={ArrowRightIconPath} />
      </MediaLinkRow>
    </CardWrapper>
  );
}

// ---------------------------------------------------------------------------
// Public export
// ---------------------------------------------------------------------------

interface Props {
  cards: RecommendedActionCard[];
}

export function RecommendedActionCards({ cards }: Props) {
  const storeId = useStoreId();
  const { data: mediasData } = useMediasByStoreId(storeId);

  const nameToIdMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of mediasData?.medias ?? []) {
      if (m.id && m.name) map.set(String(m.name).trim().toLowerCase(), m.id);
    }
    return map;
  }, [mediasData]);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  if (cards.length === 0) return null;

  return (
    <>
      {cards.slice(0, visibleCount).map((card, i) => {
        const mediaId =
          card.media_id ??
          nameToIdMap.get(card.media_name.trim().toLowerCase()) ??
          null;
        return (
          <RecommendedActionCardItem
            key={`${card.media_name}-${i}`}
            card={card}
            mediaId={mediaId}
          />
        );
      })}
      {visibleCount < cards.length && (
        <LoadMore
          totalCount={cards.length}
          loadedItemsCount={Math.min(visibleCount, cards.length)}
          onLoadMoreClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
        />
      )}
    </>
  );
}
